import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Plus } from "lucide-react";
import type { AdminUserListItem } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Badge } from "@/shared/components/ui/Badge";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Input } from "@/shared/components/ui/Input";
import { Pagination } from "@/shared/components/ui/Pagination";
import { SelectInput } from "@/shared/components/ui/SelectInput";
import { UsersTableSkeleton } from "@/routes/app/admin/users/UsersTableSkeleton";
import {
  GRID_TEMPLATE,
  ROW_LINK_SPAN,
} from "@/routes/app/admin/users/usersTableLayout";
import { useAdminUsers, type AdminUsersParams } from "@/shared/lib/admin";
import { cn } from "@/shared/lib/utils";

const PAGE_LIMIT = 50;

type StatusFilter = "all" | "active" | "suspended";
type RoleFilter = "all" | "admin" | "user";

export function AdminUsersList() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [role, setRole] = useState<RoleFilter>("all");
  const [page, setPage] = useState(1);

  function resetPaging() {
    setPage(1);
  }

  const params: AdminUsersParams = {
    search: search || undefined,
    status: status === "all" ? undefined : status,
    role: role === "all" ? undefined : role,
    page,
    limit: PAGE_LIMIT,
  };
  const query = useAdminUsers(params);
  const items = query.data?.items ?? [];
  const pageCount = Math.max(
    1,
    Math.ceil((query.data?.total ?? 0) / PAGE_LIMIT),
  );

  // Snap back if the result set shrank past the current page. Guarded on
  // `query.data` so a refetching page doesn't reset us to page 1.
  useEffect(() => {
    if (query.data && page > pageCount) setPage(pageCount);
  }, [query.data, page, pageCount]);

  function goToPage(next: number) {
    setPage(next);
    // Deferred to the next frame so the scroll runs after the new rows commit;
    // a synchronous swap to an already-cached page would otherwise cancel it.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  return (
    <Section spacing="md">
      <Container size="6xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to="/app/admin"
              className="inline-flex items-center gap-1 type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
              Admin
            </Link>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="type-heading-lg text-text">Users</h1>
              <Button asChild>
                <Link to="/app/admin/users/new">
                  <Plus aria-hidden className="h-4 w-4" />
                  New user
                </Link>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              type="search"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPaging();
              }}
              className="sm:max-w-sm"
            />
            <div className="flex gap-3">
              <SelectInput
                aria-label="Filter by status"
                className="w-40"
                value={status}
                onValueChange={(v) => {
                  setStatus(v as StatusFilter);
                  resetPaging();
                }}
                options={[
                  { value: "all", label: "All statuses" },
                  { value: "active", label: "Active" },
                  { value: "suspended", label: "Suspended" },
                ]}
              />
              <SelectInput
                aria-label="Filter by role"
                className="w-36"
                value={role}
                onValueChange={(v) => {
                  setRole(v as RoleFilter);
                  resetPaging();
                }}
                options={[
                  { value: "all", label: "All roles" },
                  { value: "admin", label: "Admins" },
                  { value: "user", label: "Users" },
                ]}
              />
            </div>
          </div>

          <div
            className={cn(
              "transition-opacity duration-300",
              query.isPlaceholderData && "opacity-60",
            )}
          >
            {query.isLoading ? (
              <UsersTableSkeleton rows={8} />
            ) : query.isError ? (
              <p className="type-body-base text-danger-strong">
                Couldn't load users: {query.error.message}
              </p>
            ) : items.length === 0 ? (
              <EmptyState
                title="No matching users"
                description="Try a different search or clear the filters."
              />
            ) : (
              <UsersTable items={items} />
            )}
          </div>

          {pageCount > 1 && !query.isLoading ? (
            <div className="flex flex-col items-center gap-2">
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={goToPage}
              />
              <p className="type-body-sm text-text-muted">
                Page {page} of {pageCount}
              </p>
            </div>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}

function UsersTable({ items }: { items: AdminUserListItem[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border-muted glass-surface-subtle">
      <div
        className={cn(
          "grid items-center gap-4 border-b border-border-muted px-4 py-2.5",
          "type-label-sm tracking-wider uppercase text-text-muted",
          GRID_TEMPLATE,
        )}
      >
        <span>Name</span>
        <span className="hidden sm:block">Email</span>
        <span>Status</span>
        <span className="hidden lg:block">Role</span>
        <span className="hidden lg:block">Created</span>
      </div>
      <ul className="flex flex-col">
        {items.map((u) => (
          <li
            key={u.id}
            className={cn(
              "grid items-center gap-4 border-t border-border-muted px-4 first:border-t-0",
              "transition-colors duration-150 hover:bg-surface-muted/40",
              GRID_TEMPLATE,
            )}
          >
            <Link
              to={`/app/admin/users/${u.id}`}
              className={cn(
                "grid grid-cols-subgrid items-center py-3",
                ROW_LINK_SPAN,
              )}
            >
              <span className="truncate type-body-base text-text">
                {u.name || "—"}
              </span>
              <span className="hidden truncate type-body-sm text-text-muted sm:block">
                {u.email}
              </span>
              <span className="flex items-center">
                {u.suspendedAt ? (
                  <Badge className="text-danger-strong">Suspended</Badge>
                ) : (
                  <Badge variant="muted">Active</Badge>
                )}
              </span>
              <span className="hidden lg:flex">
                {u.isAdmin ? (
                  <Badge>Admin</Badge>
                ) : (
                  <span className="type-body-sm text-text-muted">User</span>
                )}
              </span>
              <span className="hidden type-body-sm text-text-muted lg:block">
                {new Date(u.createdAt).toLocaleDateString()}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
